import { useMemo } from 'react';
import { FolderTree } from '@/hooks/useFolders';

/**
 * Hook to build a breadcrumb path for folder navigation.
 * Traverses the folder tree to find the path from root to the current folder.
 *
 * @param currentFolder - The currently selected folder (null for "All Files")
 * @param folderTree - The hierarchical folder tree structure
 * @returns Array of folders representing the path from root to current folder
 */
export function useBreadcrumbPath(
  currentFolder: FolderTree | null,
  folderTree: FolderTree[]
): FolderTree[] {
  return useMemo(() => {
    if (!currentFolder) return [];

    // Find ancestors by traversing down the tree
    const findPath = (
      folders: FolderTree[],
      targetGuid: string,
      currentPath: FolderTree[]
    ): FolderTree[] | null => {
      for (const folder of folders) {
        if (folder.guid === targetGuid) {
          return [...currentPath, folder];
        }
        if (folder.children && folder.children.length > 0) {
          const found = findPath(folder.children, targetGuid, [...currentPath, folder]);
          if (found) return found;
        }
      }
      return null;
    };

    return findPath(folderTree, currentFolder.guid || '', []) || [currentFolder];
  }, [currentFolder, folderTree]);
}
